/**
 * The per-group switch "Neues Snippet-System" (2026-09-22).
 *
 * Which groups get the new v2 catalog is a decision the founder makes per
 * group in the account management, not something every customer gets at
 * once. With the switch on, the editor sidebar shows ONLY what comes from
 * the database (the v2 world: containers, elements, snippets, the group's
 * own rows) and hides the classic file snippets (V1). Rows marked
 * `active: false` -- "not released to everyone yet" -- become visible for
 * such a pilot group, the same way they are for a sysadmin. Rows marked
 * `legacy: true` -- the elements from before the designs -- are hidden from
 * it; everyone else keeps them.
 *
 * With the switch off nothing changes: V1 file snippets plus the released
 * DB rows, drafts for sysadmins only.
 *
 * The switch only means something inside the Vue 3 editor; the classic
 * editor cannot render v2 elements at all, so it is ignored there.
 */
export const V2_CATALOG_ONLY = 'v2_catalog_only';

/**
 * "Seiten als Modell speichern" (2026-09-22, JSON stage 2): the V2 block of
 * an element is the truth and its html is rendered from it, on load and on
 * save -- for every element whose block passes the gates. Own switch, so it
 * can be turned back without touching the catalog. Vue 3 editor only.
 */
export const PAGES_AS_MODEL = 'pages_as_model';

export function isPagesAsModel(groupSettings) {
  return !!(groupSettings && groupSettings.enable_vue3_editor === true && groupSettings[PAGES_AS_MODEL] === true);
}

export function isV2CatalogOnly(groupSettings) {
  return !!(groupSettings && groupSettings.enable_vue3_editor === true && groupSettings[V2_CATALOG_ONLY] === true);
}

/**
 * Mischweg (spec 2026-09-23, plan index note 13): Bereiche, their catalog
 * rows, panel and AI entries appear only with BOTH switches on.
 */
export function isBereichAktiv(groupSettings) {
  return isPagesAsModel(groupSettings) && isV2CatalogOnly(groupSettings);
}

/**
 * What the sidebar loader may show for this group and user.
 * - `staticSnippets`: fetch and list the classic V1 file snippets.
 * - `drafts`: list DB rows whose `data.active === false`.
 * - `legacy`: list DB rows whose `data.legacy === true`.
 * - `layoutTab`: the sidebar's "Layout" tab (the old page layout picker).
 */
export function catalogVisibility(groupSettings, isSysAdmin) {
  const pilot = isV2CatalogOnly(groupSettings);
  return {
    staticSnippets: !pilot,
    drafts: isSysAdmin === true || pilot,
    legacy: !pilot,
    layoutTab: !pilot,
  };
}

/**
 * Effective editor settings of a project (spec 2026-10-15): the project's
 * `editor_version` (1 = classic, 2 = Vue 3 editor, 3 = Vue 3 editor with
 * pages as model) overrides the group's switches. Returns a copy. A missing
 * version (folder pages have no project) leaves the group settings as they are.
 * Versions 1 and 2 also drop `v2_catalog_only`, so a v2 project in a v3 group
 * keeps the V1 snippets and the Layout tab.
 */
export function settingsForVersion(groupSettings, version) {
  const settings = { ...(groupSettings || {}) };
  if (version !== 1 && version !== 2 && version !== 3) return settings;
  settings.enable_vue3_editor = version >= 2;
  settings[PAGES_AS_MODEL] = version === 3;
  if (version < 3) settings[V2_CATALOG_ONLY] = false;
  return settings;
}
